import { type FC, useEffect, useState } from 'react';
import MobileFiltersToggle from './MobileFiltersToggle';
import Button from '2-core-elements/Buttons/Button';
import { type SimpleFilter } from '../EventCalendarContext';
import Icon from '2-core-elements/Icon/Icon';
import LinkCTA from '2-core-elements/LinkCTA/LinkCTA';
import { normalizeFilterValue } from '../QuerystringHelpers';
import Tag from '2-core-elements/Tag/Tag';
import { pushGTMEvent } from 'utils/eventGTM';

interface EventFiltersProps {
    isOpen: boolean;
    onClose: () => void;
    onToggle: () => void;
    calendarRef?: React.RefObject<HTMLElement>;
    filters?: SimpleFilter[];
    activeFilters?: Record<string, string[]>;
    onFiltersChange?: (filters: Record<string, string[]>) => void;
    totalResults: number;
}

const EventFilters: FC<EventFiltersProps> = ({
    isOpen,
    onClose,
    onToggle,
    calendarRef,
    filters = [],
    activeFilters = {},
    onFiltersChange,
    totalResults,
}) => {
    const [openFilters, setOpenFilters] = useState<string[]>([]);

    const mappedFilter = filters?.map(filter => {
        return {
            ...filter,
            values: Array.isArray(filter.values) ? filter.values.map(v => {
                return typeof v === 'string' ? v : v.name;
            }) : [],
            activeDefaultValues: Array.isArray(filter.activeDefaultValues) ? filter.activeDefaultValues.map(v => {
                return typeof v === 'string' ? v : v.name;
            }) : [],
        };
    });

    const scrollToEventsWrapper = (): void => {
        const eventsWrapper = document.querySelector('.events-wrapper');
        if (eventsWrapper) {
            const elementTop = eventsWrapper.getBoundingClientRect().top + window.pageYOffset;

            // Dynamically calculate offset for sticky headers
            let navOffset = 0;
            const eventNavBar = document.querySelector('.event-nav-bar');
            const bannerTabs = document.querySelector('.event-landing-banner-tabs');

            if (eventNavBar) {
                navOffset += eventNavBar.getBoundingClientRect().height;
            }
            if (bannerTabs) {
                navOffset += bannerTabs.getBoundingClientRect().height;
            }

            // Add some padding for better visual spacing (desktop only)
            const isDesktop = window.innerWidth >= 768;
            if (isDesktop) {
                navOffset += 20;
            }

            window.scrollTo({
                top: elementTop - navOffset,
                behavior: 'smooth',
            });
        } else {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    };

    const handleShowResults = (): void => {
        const isDesktop = window.innerWidth >= 768;

        if (isDesktop) {
            scrollToEventsWrapper();
        } else {
            onClose();
            // Delay to allow modal to close/animate before scrolling
            setTimeout(() => {
                scrollToEventsWrapper();
            }, 300);
        }
    };

    // Toggle filter options
    const toggleFilter = (filterName: string): void => {
        setOpenFilters(prev => {
            if (prev.includes(filterName)) {
                return prev.filter(name => {
                    return name !== filterName;
                });
            }
            return [...prev, filterName];
        });
    };

    // Handle filter value change
    const handleFilterChange = (filterName: string, value: string, isChecked: boolean): void => {
        if (!onFiltersChange) {
            return;
        }

        // Normalize to canonical key and value casing
        const { normalizedFilterName, normalizedValue } = normalizeFilterValue(filterName, value, mappedFilter || []);

        // Reuse any existing key that matches case-insensitively
        const existingKey = Object.keys(activeFilters || {}).find(k => {
            return k.toLowerCase() === normalizedFilterName.toLowerCase();
        });
        const key = existingKey ?? normalizedFilterName;

        const currentValues = activeFilters?.[key] || [];
        const contains = currentValues.some(v => {
            return v.toLowerCase() === normalizedValue.toLowerCase();
        });

        let nextValues: string[];
        if (isChecked) {
            nextValues = contains ? currentValues : [...currentValues, normalizedValue];
        } else {
            nextValues = currentValues.filter(v => {
                return v.toLowerCase() !== normalizedValue.toLowerCase();
            });
        }

        const nextFilters = { ...(activeFilters || {}) };
        if (nextValues.length > 0) {
            nextFilters[key] = nextValues;
        } else {
            delete nextFilters[key];
        }

        // Optionally migrate to canonical key casing
        if (existingKey && existingKey !== normalizedFilterName && nextFilters[existingKey]) {
            nextFilters[normalizedFilterName] = nextFilters[existingKey];
            delete nextFilters[existingKey];
        }

        onFiltersChange(nextFilters);
    };
    // Prevent body scroll when mobile filters are open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return (): void => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    return (
        <>
            {isOpen && (
                <div
                    className="mobile-filters-overlay"
                    onClick={onClose}
                />
            )}

            <div className={`event-filters ${isOpen ? 'open' : ''}`}>
                <div className="mobile-filters-header">
                    <div className="mobile-filters-title">Filters</div>
                    <Button
                        className="mobile-filters-close"
                        prefixIcon="close"
                        onlyIcon={true}
                        onClick={onClose}
                        aria-label="Close filters"
                        skin="2"
                    />
                </div>

                <div className="filters-content">
                    {Object.keys(activeFilters).length > 0 && (
                        <div className="active-filters-section">
                            <div className="active-filter-tags">
                                {Object.entries(activeFilters).map(([filterName, filterValues]) => {
                                    return filterValues.map(value => {
                                        // Use helper to get normalized display values
                                        const { normalizedValue } = normalizeFilterValue(filterName, value, mappedFilter || []);
                                        return (
                                            <Tag
                                                key={`${filterName}-${value}`}
                                                label={normalizedValue}
                                                onClick={(): void => {
                                                    handleFilterChange(filterName, value, false);
                                                    pushGTMEvent({
                                                        event: 'remove',
                                                        actionType: 'click_filter',
                                                        search_term: '',
                                                        search_type: 'calendar',
                                                        search_subtype: filterName,
                                                        filter: value,
                                                    });
                                                }}
                                                aria-label={`Remove filter: ${filterName} = ${value}`}
                                                onKeyDown={(e): void => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleFilterChange(filterName, value, false);
                                                    }
                                                }}
                                                showDismissIcon={true}
                                                // prefixIcon="tag"
                                                size="sm"
                                                type="primary"
                                                tagStyle="bold"
                                            />
                                        );
                                    });
                                }
                                )}
                            </div>
                            <LinkCTA
                                className="clear-all-filters"
                                skin="1"
                                size="sm"
                                onClick={(): void => {
                                    if (onFiltersChange) {
                                        onFiltersChange({});
                                    }
                                }}
                            >
                                Clear All
                            </LinkCTA>
                        </div>
                    )}
                    {mappedFilter
                        .filter(filter => {
                            return filter.values && filter.values.length > 0;
                        })
                        .map(filter => {
                            const filterIsOpen = openFilters.includes(filter.name);
                            return (
                                <div key={filter.name}>
                                    <div
                                        className={`filter-header ${filterIsOpen ? 'is-open' : ''}`}
                                        onClick={(): void => {
                                            toggleFilter(filter.name);
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e): void => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleFilter(filter.name);
                                            }
                                        }}
                                    >
                                        <span>{filter.name}</span>
                                        <Icon icon="chevron-down" />
                                    </div>
                                    {filterIsOpen && (
                                        <div className="filter-options">
                                            {filter.values?.map((item: string) => {
                                                // Case-insensitive matching to handle URL parsing differences
                                                const matchingFilterKey = Object.keys(activeFilters).find(key => {
                                                    return key.toLowerCase() === filter.name.toLowerCase();
                                                });

                                                const isChecked = matchingFilterKey
                                                    ? activeFilters[matchingFilterKey]?.some(val => {
                                                        return val.toLowerCase() === item.toLowerCase();
                                                    }) || false
                                                    : false;
                                                return (
                                                    <div
                                                        key={item}
                                                        className="filter-option"
                                                        role="checkbox"
                                                        aria-checked={isChecked}
                                                        tabIndex={0}
                                                        onClick={(): void => {
                                                            handleFilterChange(filter.name, item, !isChecked);

                                                            pushGTMEvent({
                                                                event: !isChecked ? 'select' : 'remove',
                                                                actionType: 'click_filter',
                                                                search_term: '',
                                                                search_type: 'calendar',
                                                                search_subtype: filter.name,
                                                                filter: item,
                                                            });
                                                        }}
                                                        onKeyDown={(e): void => {
                                                            if (e.key === ' ' || e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleFilterChange(filter.name, item, !isChecked);

                                                                pushGTMEvent({
                                                                    event: !isChecked ? 'select' : 'remove',
                                                                    actionType: 'click_filter',
                                                                    search_term: '',
                                                                    search_type: 'calendar',
                                                                    search_subtype: filter.name,
                                                                    filter: item,
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <div className={`custom-checkbox ${isChecked ? 'checked' : ''}`}>
                                                            {isChecked && <Icon icon="check" />}
                                                        </div>
                                                        <span>{item}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
                <div className="show-all-results-wrapper">
                    <Button
                        skin="1"
                        className="show-all-results"
                        onClick={handleShowResults}
                        type="button"
                    >
                        Show {totalResults} Results
                    </Button>
                </div>
            </div>
            <MobileFiltersToggle onToggle={onToggle} calendarRef={calendarRef} isOpen={isOpen} totalResults={totalResults} />
        </>
    );
};

export default EventFilters;
